import type { FlightFilterType, FlightSortOptions } from 'constants/flight'
import type { ValueOf } from 'type-fest'
import { removeUnusedFilters } from 'lib/flights/flightFilterUtils'
import type { FlightsFiltersValues } from 'lib/flights/flightFilterTypes'
import { FlightsFiltersPriceType } from 'lib/flights/flightFilterTypes'

export enum StandaloneFlightsFiltersAction {
  SET_FLIGHTS_FILTER = 'SET_FLIGHTS_FILTER',
  SET_FLIGHTS_SORT = 'SET_FLIGHTS_SORT',
  RE_INITIALISE = 'RE_INITIALISE',
  RESET_FILTERS = 'RESET_FILTERS',
  RESET_FILTER = 'RESET_FILTER',
  TOGGLE_NON_EXISTENT_FILTERS = 'TOGGLE_NON_EXISTENT_FILTERS',
  CLEAR_FILTER_VALUE = 'CLEAR_FILTER_VALUE',
}

export type StandaloneFlightsFiltersActions = ValueOf<Utils.FullActionMap<{
  [StandaloneFlightsFiltersAction.SET_FLIGHTS_FILTER]: { filters: Partial<FlightsFiltersValues> }
  [StandaloneFlightsFiltersAction.SET_FLIGHTS_SORT]: { sort: FlightSortOptions }
  [StandaloneFlightsFiltersAction.RESET_FILTERS]: {}
  [StandaloneFlightsFiltersAction.RESET_FILTER]: { filterType: FlightFilterType }
  [StandaloneFlightsFiltersAction.RE_INITIALISE]: { initialState: Partial<FlightsFiltersValues> }
  [StandaloneFlightsFiltersAction.TOGGLE_NON_EXISTENT_FILTERS]: { filters: Partial<FlightsFiltersValues> }
  [StandaloneFlightsFiltersAction.CLEAR_FILTER_VALUE]: { filterType: FlightFilterType, value: string }
}>>

export interface StandaloneFlightFiltersState extends FlightsFiltersValues {}

export const initialState: StandaloneFlightFiltersState = {
  stops: [],
  airlines: [],
  cabinTypes: [],
  carriers: [],
  stopDurations: undefined,
  durations: undefined,
  priceFilterType: FlightsFiltersPriceType.ADULT,
  recommended: [],
  bags: [],
  stopOverCities: [],
  alliances: [],
  maxArrivalTime: undefined,
  maxDepartureTime: undefined,
  maxPrices: undefined,
  minArrivalTime: undefined,
  minDepartureTime: undefined,
  minPrices: undefined,
  sort: undefined,
}

function standaloneFlightFiltersReducer(state: StandaloneFlightFiltersState, action: StandaloneFlightsFiltersActions): StandaloneFlightFiltersState {
  switch (action.type) {
    case StandaloneFlightsFiltersAction.SET_FLIGHTS_FILTER:
      return {
        ...state,
        ...action.filters,
      }

    case StandaloneFlightsFiltersAction.RESET_FILTERS:
      return {
        ...initialState,
      }

    case StandaloneFlightsFiltersAction.RE_INITIALISE:
      return {
        ...initialState,
        ...action.initialState,
      }

    case StandaloneFlightsFiltersAction.RESET_FILTER:
      return {
        ...state,
        [action.filterType]: initialState[action.filterType as keyof FlightsFiltersValues],
      }

    case StandaloneFlightsFiltersAction.TOGGLE_NON_EXISTENT_FILTERS: {
      const newFilters = Object.keys(action.filters).reduce((filters, filterKey) => ({
        ...filters,
        [filterKey]: removeUnusedFilters(state[filterKey as keyof FlightsFiltersValues] || [], action.filters[filterKey as keyof FlightsFiltersValues] as Array<string>),
      }), {} as Partial<FlightsFiltersValues>)

      return {
        ...state,
        ...newFilters,
      }
    }

    case StandaloneFlightsFiltersAction.SET_FLIGHTS_SORT: {
      return {
        ...state,
        sort: action.sort,
      }
    }

    case StandaloneFlightsFiltersAction.CLEAR_FILTER_VALUE:
      const existingFilterValue = state[action.filterType as keyof FlightsFiltersValues]
      const isArray = Array.isArray(existingFilterValue)

      return {
        ...state,
        [action.filterType]: isArray ? existingFilterValue?.filter(value => value !== action.value) : undefined,
        recommended: state.recommended.filter(value => value !== action.value),
      }

    default:
      return state
  }
}

export default standaloneFlightFiltersReducer
